/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export type CalculatorType = 'brutto-netto' | 'einkommensteuer' | 'sparrechner' | 'kreditrechner' | 'minijob' | 'wohngeld';

// General Bundesland definition
export interface Bundesland {
  id: string;
  name: string;
  kircheRate: number; // 0.08 or 0.09
}

export const BUNDESLAENDER: Bundesland[] = [
  { id: 'BW', name: 'Baden-W\u00FCrttemberg', kircheRate: 0.08 },
  { id: 'BY', name: 'Bayern', kircheRate: 0.08 },
  { id: 'BE', name: 'Berlin', kircheRate: 0.09 },
  { id: 'BB', name: 'Brandenburg', kircheRate: 0.09 },
  { id: 'HB', name: 'Bremen', kircheRate: 0.09 },
  { id: 'HH', name: 'Hamburg', kircheRate: 0.09 },
  { id: 'HE', name: 'Hessen', kircheRate: 0.09 },
  { id: 'MV', name: 'Mecklenburg-Vorpommern', kircheRate: 0.09 },
  { id: 'NI', name: 'Niedersachsen', kircheRate: 0.09 },
  { id: 'NW', name: 'Nordrhein-Westfalen', kircheRate: 0.09 },
  { id: 'RP', name: 'Rheinland-Pfalz', kircheRate: 0.09 },
  { id: 'SL', name: 'Saarland', kircheRate: 0.09 },
  { id: 'SN', name: 'Sachsen', kircheRate: 0.09 },
  { id: 'ST', name: 'Sachsen-Anhalt', kircheRate: 0.09 },
  { id: 'SH', name: 'Schleswig-Holstein', kircheRate: 0.09 },
  { id: 'TH', name: 'Th\u00FCringen', kircheRate: 0.09 },
];

export interface BruttoNettoInputs {
  bruttogehalt: number;
  jahr: number; // 2025 or 2026
  steuerklasse: number; // 1 to 6
  bundesland: string; // state code
  kirchensteuer: boolean;
  kinder: boolean;
  kinderZahl: number;
  zusatzbeitrag: number; // e.g., 1.7%
}

export interface BruttoNettoResult {
  brutto: number;
  rentenversicherung: number;
  arbeitslosenversicherung: number;
  krankenversicherung: number;
  pflegeversicherung: number;
  sozialabgaben: number;
  lohnsteuer: number;
  soli: number;
  kirchensteuer: number;
  steuern: number;
  netto: number;
  arbeitgeberAnteil: number;
  gesamtKostenArbeitgeber: number;
}

export interface EinkommensteuerInputs {
  einkommen: number;
  jahr: number; // 2025 or 2026
  veranlagung: 'einzeln' | 'gemeinsam';
  kirchensteuer: boolean;
  bundesland: string;
}

export interface EinkommensteuerResult {
  einkommen: number;
  grundfreibetrag: number;
  steuer: number;
  soli: number;
  kirchensteuer: number;
  gesamtbelastung: number;
  netto: number;
  durchschnittssteuersatz: number;
  grenzsteuersatz: number;
}

export interface SparrechnerInputs {
  anfangskapital: number;
  sparrate: number;
  zinssatz: number;
  laufzeit: number; // in Jahren
  zinseszins: boolean;
}

export interface SparrechnerResult {
  anfangskapital: number;
  einzahlungen: number;
  zinsgewinn: number;
  endkapital: number;
  yearlyData: {
    jahr: number;
    einzahlungen: number;
    zinsen: number;
    guthaben: number;
  }[];
}

export interface KreditrechnerInputs {
  kreditsumme: number;
  zinssatz: number; // Sollzins in % p.a.
  tilgungssatz: number; // Anf\u00E4ngliche Tilgung in % p.a.
  zinsbindung: number; // Zinsbindung in Jahren
}

export interface KreditrechnerResult {
  monatlicheRate: number;
  zinsbindungJahre: number;
  bezahlteZinsen: number;
  getilgteSumme: number;
  restschuld: number;
  monatlicheZinsenAnfang: number;
  monatlicheTilgungAnfang: number;
  yearlyPlan: {
    jahr: number;
    rateGesamt: number;
    zinsAnteil: number;
    tilgungsAnteil: number;
    restschuld: number;
  }[];
}

export interface MinijobInputs {
  bruttogehalt: number;
  rentenversicherungspflicht: boolean; // default true
}

export interface MinijobResult {
  isMinijob: boolean; // up to 538 in 2024, 556 in 2026/2025
  isMidijob: boolean; // 538.01 to 2000
  rvSatzArbeitnehmer: number; // worker share %
  rvBeitragArbeitnehmer: number; // EUR
  abgabenArbeitnehmer: number;
  netto: number;
  abgabenArbeitgeber: number; // flat employer rates for minijob (~30%)
  gesamtKostenArbeitgeber: number;
}

export interface SeveranceInputs {
  taxYear: number;
  severancePay: number;
  annualGross: number; // Laufendes zu versteuerndes Einkommen (zvE)
  wageReplacement: number; // Lohnersatzleistungen
  churchTax: 'none' | '8%' | '9%';
  jointTaxation: boolean;
}

export type AssetType = 'zinsen' | 'mischfonds' | 'aktienfonds' | 'immo_inland' | 'immo_ausland';

export interface AbgeltungssteuerInputs {
  kapitalertrag: number; // Capital gains in EUR
  assetType: AssetType; // Type of investment
  sparerpauschbetrag: number; // Applied Freibetrag (0, 1000, 2000 or custom)
  churchTax: 'none' | '8%' | '9%'; // Church tax rate
  personalTaxRate?: number; // Optional personal marginal tax rate for Guenstigerpruefung (0-45%)
}

export interface AbgeltungssteuerResult {
  kapitalertrag: number;
  teilfreistellungProzent: number;
  teilfreistellungBetrag: number;
  ertragNachTeilfreistellung: number;
  sparerpauschbetragAngewendet: number;
  zuVersteuernderErtrag: number;
  abgeltungssteuerSatz: number;
  abgeltungssteuer: number;
  soli: number;
  kirchensteuer: number;
  gesamtSteuer: number;
  effektiverSteuersatz: number;
  nettoErtrag: number;
  guenstigerpruefungEmpfehlung: boolean;
  steuerBeiPersoenlichemSatz?: number;
  ersparnisGuenstigerpruefung?: number;
}

export interface SeveranceResult {
  taxYear: number;
  severancePay: number;
  annualGross: number;
  wageReplacement: number;
  isJoint: boolean;
  
  taxNormal: number;
  soliNormal: number;
  churchTaxNormal: number;
  totalTaxNormal: number;
  
  taxStandard: number;
  soliStandard: number;
  churchTaxStandard: number;
  totalTaxStandard: number;
  addedTaxStandard: number;
  
  taxWithFifth: number;
  soliWithFifth: number;
  churchTaxWithFifth: number;
  totalTaxWithFifth: number;
  
  taxOnSeverancePay: number;
  soliOnSeverancePay: number;
  churchTaxOnSeverancePay: number;
  totalTaxOnSeverancePay: number;
  
  taxSavings: number;
  netSeverancePay: number;
  effectiveSeveranceTaxRate: number;
  
  grandTotalTax: number;
  grandTotalSoli: number;
  grandTotalChurchTax: number;
  grandTotalPayment: number;
  grandTotalNetRatio: number;
}

export interface AussergewoehnlicheBelastungenInputs {
  expenses: number;
  taxYear: number;
  income: number;
  familyStatus: 'single' | 'married' | 'single_parent';
  childrenCategory: 'none' | '1-2' | '3+';
  churchTaxRate: number; // 0, 8, 9
}

export interface StufenDetail {
  tierName: string;
  baseAmount: number;
  rate: number;
  tierAmount: number;
}

export interface AussergewoehnlicheBelastungenResult {
  expenses: number;
  income: number;
  taxYear: number;
  familyStatus: 'single' | 'married' | 'single_parent';
  childrenCategory: 'none' | '1-2' | '3+';
  churchTaxRate: number;

  // Zumutbare Belastung
  zumutbareBelastung: number;
  tiers: StufenDetail[];

  // Abzugsfähiger Betrag
  deductibleAmount: number;
  isOverLimit: boolean;

  // Grundtarif (Einzelveranlagung)
  taxBeforeGrund: number;
  soliBeforeGrund: number;
  churchBeforeGrund: number;
  totalTaxBeforeGrund: number;

  taxAfterGrund: number;
  soliAfterGrund: number;
  churchAfterGrund: number;
  totalTaxAfterGrund: number;

  taxSavingsGrund: number;
  marginalRateGrund: number;

  // Splittingtarif (Zusammenveranlagung)
  taxBeforeSplitting: number;
  soliBeforeSplitting: number;
  churchBeforeSplitting: number;
  totalTaxBeforeSplitting: number;

  taxAfterSplitting: number;
  soliAfterSplitting: number;
  churchAfterSplitting: number;
  totalTaxAfterSplitting: number;

  taxSavingsSplitting: number;
  marginalRateSplitting: number;
}

export interface BruttoRechnerInputs {
  wunschNetto: number;
  zeitraum: 'monat' | 'jahr';
  jahr: number; // 2027, 2026, 2025
  geburtsjahr: string; // 'nach 1961' | 'vor 1962'
  kinderfreibetrag: string; // '0.0', '0.5', '1.0', '1.5', '2.0', '2.5', '3.0', '4.0'
  steuerklasse: number; // 1 to 6
  kinderStatus: string; // 'kinderlos_ab_23' | 'kinderlos_unter_23' | 'eltern_1' | 'eltern_2' | 'eltern_3' | 'eltern_4' | 'eltern_5_plus'
  bundesland: string; // e.g. 'BW', 'BY', 'SN'
  kirchensteuer: 'ja' | 'nein';
  rentenversicherungspflicht: 'ja' | 'nein';
  arbeitslosenversicherungspflicht: 'ja' | 'nein';
  krankenversicherung: 'gesetzlich' | 'erm\u00E4\u00DFigt' | 'privat';
  zusatzbeitrag: number; // e.g. 2.9
  freibetragLSt: number; // optional monthly allowance
  hinzurechnungsbetrag: number;
  privateKVBeitrag: number;
  umlagen: 'ja' | 'nein';
  wochenstunden?: number;
}

export interface BruttoRechnerResult {
  benoetigtesBruttoMonat: number;
  benoetigtesBruttoJahr: number;
  wunschNettoMonat: number;
  wunschNettoJahr: number;

  // Taxes
  lohnsteuerMonat: number;
  lohnsteuerJahr: number;
  soliMonat: number;
  soliJahr: number;
  kirchensteuerMonat: number;
  kirchensteuerJahr: number;
  steuernGesamtMonat: number;
  steuernGesamtJahr: number;

  // Social Contributions (Employee)
  rentenversicherungMonat: number;
  rentenversicherungJahr: number;
  arbeitslosenversicherungMonat: number;
  arbeitslosenversicherungJahr: number;
  krankenversicherungMonat: number;
  krankenversicherungJahr: number;
  pflegeversicherungMonat: number;
  pflegeversicherungJahr: number;
  sozialabgabenGesamtMonat: number;
  sozialabgabenGesamtJahr: number;

  // Total Deductions
  abzuegeGesamtMonat: number;
  abzuegeGesamtJahr: number;
  nettoQuote: number;
  abzugsQuote: number;

  // Employer Total Cost
  arbeitgeberAnteilMonat: number;
  gesamtKostenArbeitgeberMonat: number;
  gesamtKostenArbeitgeberJahr: number;

  // Hourly wage equivalents
  stundenlohnBrutto: number;
  stundenlohnNetto: number;

  // Salary Negotiation Puffer
  verhandlung: {
    basisBrutto: number;
    puffer5: number;
    puffer10: number;
    puffer15: number;
  };
}

export type Verwandtschaftsgrad = 
  | 'ehegatte' // Steuerklasse I, Freibetrag 500.000 € + Versorgungsfreibetrag max 256.000 €
  | 'lebenspartner' // Steuerklasse I, Freibetrag 500.000 € + Versorgungsfreibetrag max 256.000 €
  | 'kind' // Steuerklasse I, Freibetrag 400.000 € + Versorgungsfreibetrag bis 52.000 €
  | 'stiefkind' // Steuerklasse I, Freibetrag 400.000 € + Versorgungsfreibetrag bis 52.000 €
  | 'enkel' // Steuerklasse I, Freibetrag 200.000 € (bzw. 400.000 € bei verstorbenem Kind)
  | 'urenkel' // Steuerklasse I, Freibetrag 100.000 €
  | 'eltern' // Steuerklasse I, Freibetrag 100.000 € (im Erbfall von Todes wegen)
  | 'grosseltern' // Steuerklasse I, Freibetrag 100.000 € (im Erbfall)
  | 'geschwister' // Steuerklasse II, Freibetrag 20.000 €
  | 'nichten_neffen' // Steuerklasse II, Freibetrag 20.000 €
  | 'stiefeltern' // Steuerklasse II, Freibetrag 20.000 €
  | 'schwiegereltern' // Steuerklasse II, Freibetrag 20.000 €
  | 'schwiegerkinder' // Steuerklasse II, Freibetrag 20.000 €
  | 'geschiedener_ehegatte' // Steuerklasse II, Freibetrag 20.000 €
  | 'nicht_verwandt'; // Steuerklasse III, Freibetrag 20.000 € (Lebensgefährte, Freunde, etc.)

export type ImmobilienNutzung = 
  | 'keine'
  | 'familienheim_selbst' // § 13 Abs. 1 Nr. 4b/4c ErbStG: 10 Jahre Selbstnutzung
  | 'vermietet_wohnzwecke' // § 13d ErbStG: 10% Bewertungsabschlag (90% steuerpflichtig)
  | 'gewerbe_oder_sonstige'; // 100% steuerpflichtig

export type BetriebsvermoegenOption =
  | 'keine'
  | 'regelverschonung' // 85% Verschonung + gleitender Abzugsbetrag bis 150.000 €
  | 'optionsverschonung'; // 100% Verschonung

export interface ErbschaftsteuerInputs {
  erbfallJahr: number; // 2027, 2026, 2025
  verwandtschaft: Verwandtschaftsgrad;
  enkelElternteilVerstorben?: boolean;
  kindAlterJahre?: number; // 0-27 für gestaffelten Versorgungsfreibetrag

  // Vermögenswerte
  privatvermoegen: number; // Bankkonten, Wertpapiere, Barvermögen
  hausrat: number; // Hausrat & bewegliche Gegenstände
  zugewinnAusgleich: number; // Güterstand Zugewinngemeinschaft steuerfrei nach § 5 ErbStG

  // Immobilien
  immobilienNutzung: ImmobilienNutzung;
  immobilienWert: number;
  immobilienWohnflaeche: number; // qm (relevant bei Kindern > 200 qm)

  // Betriebsvermögen
  betriebsvermoegen: number;
  betriebsvermoegenOption: BetriebsvermoegenOption;

  // Schulden & Erbfallkosten
  schulden: number; // Erblasserschulden, Kredite, Pflichtteile
  erbfallkostenArt: 'pauschale' | 'individuell';
  erbfallkostenBetrag: number; // 15.000 € Pauschale oder Einzelnachweis

  // Besondere Freibeträge & Vorbelastungen
  pflegeleistungenWert: number; // Bis 20.000 € nach § 13 Abs. 1 Nr. 9 ErbStG
  rentenVersorgungKapitalwert: number; // Kürzung des Versorgungsfreibetrags (Witwen-/Waisenrente)
  vorerwerbe10Jahre: number; // Frühere Schenkungen innerhalb von 10 Jahren (§ 14 ErbStG)
}

export interface ErbschaftsteuerResult {
  // Steuerklasse & Grundwerte
  steuerklasse: 'I' | 'II' | 'III';
  steuerklasseNum: number;
  
  // Vermögensaufstellung
  bruttoNachlass: number; // Gesamte Aktiva
  steuerfreierHausrat: number;
  steuerfreieImmobilie: number;
  steuerfreiesBetriebsvermoegen: number;
  steuerfreierZugewinn: number;
  steuerbeguenstigtesVermoegenGesamt: number;

  // Verbindlichkeiten & Kosten
  schuldenAbzug: number;
  erbfallkostenAbzug: number;
  gesamteNachlassverbindlichkeiten: number;

  // Reiner Erwerb (Bereinigter Netto-Nachlass vor persönlichen Freibeträgen)
  reinerErwerb: number;

  // Freibeträge
  persoenlicherFreibetrag: number;
  persoenlicherFreibetragVerfuegbar: number; // Nach Anrechnung Vorerwerbe
  versorgungsfreibetrag: number;
  pflegefreibetrag: number;
  freibetragGesamt: number;
  effektivGenutzterFreibetrag: number;

  // Steuerpflichtiger Erwerb nach § 10 Abs. 1 Satz 6 ErbStG (auf volle 100 € abgerundet)
  steuerpflichtigerErwerbUnabgerundet: number;
  steuerpflichtigerErwerb: number;

  // Tarifberechnung nach § 19 ErbStG
  steuersatzProzent: number;
  tariflicheSteuer: number;
  
  // Härteausgleich (§ 19 Abs. 3 ErbStG)
  haerteausgleichAngewendet: boolean;
  haerteausgleichErsparnis: number;
  
  // Tarifentlastung für Betriebsvermögen (§ 19a ErbStG bei Klasse II & III)
  tarifentlastung19a: number;

  // Endgültige Erbschaftsteuer & Netto-Erbe
  erbschaftsteuer: number;
  effektiverSteuersatz: number;
  nettoErbschaft: number;
}

export interface AltersrechnerInputs {
  geburtsdatum: string; // YYYY-MM-DD
  stichtag: string; // YYYY-MM-DD (Defaults to current date)
  geburtszeit?: string; // HH:mm optional
}

export interface AltersrechnerMilestone {
  ageYears: number;
  title: string;
  description: string;
  dateStr: string;
  isPast: boolean;
  daysRemaining: number;
}

export interface AltersrechnerResult {
  // Primäres Alter nach BGB § 187 Abs. 2 Satz 2 & § 188
  jahre: number;
  monate: number;
  tage: number;
  
  // Exakte Gesamtsummen
  gesamttage: number;
  gesamtstunden: number;
  gesamtminuten: number;
  gesamtsekunden: number;
  gesamtWochen: number;
  wochenRestTage: number;
  
  // Wochentage & Astronomie
  wochentagGeburt: string;
  wochentagStichtag: string;
  sternzeichen: string;
  sternzeichenSymbol: string;
  chinesischesTierkreiszeichen: string;
  
  // Nächster Geburtstag
  tageBisNaechsterGeburtstag: number;
  naechsterGeburtstagDatum: string;
  wochentagNaechsterGeburtstag: string;
  wirdJahreAlt: number;

  // Besonderheiten
  istSchaltjahrGeboren: boolean;
  ist29FebruarGeboren: boolean;
  schaltjahrRegelungHinweis: string;

  // Verteilungskennzahlen
  herzschlaegeGeschaetzt: number;
  atemzuegeGeschaetzt: number;
  schlafjahreGeschaetzt: number;
  lebensfortschrittProzent80: number; // Basierend auf statistischer Lebenserwartung ~80-82 Jahre

  // Gesetzliche & persönliche Meilensteine (Volljährigkeit, Renteneintritt etc.)
  meilensteine: AltersrechnerMilestone[];
}


